import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { toast } from "sonner";
interface ContributionTier {
  title: string;
  description: string;
  amount: number;
  rewards?: string;
}
interface CampaignDetails {
  id: string;
  user_id: string;
  title: string;
  description: string;
  goal_amount: number;
  current_amount: number;
  deadline: string;
  cover_image_url: string;
  phase_images: string[];
  contribution_tiers: ContributionTier[];
}
export default function CampaignDetails() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useUser();
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchCampaign();
  }, [id]);
  const fetchCampaign = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('campaigns').select('*').eq('id', id).single();
      if (error) throw error;

      // Transform the contribution_tiers to ensure they match the expected type
      const transformedCampaign = {
        ...data,
        contribution_tiers: Array.isArray(data.contribution_tiers) ? data.contribution_tiers.map((tier: any) => ({
          title: tier.title || '',
          description: tier.description || '',
          amount: typeof tier.amount === 'number' ? tier.amount : parseFloat(tier.amount) || 0,
          rewards: tier.rewards || undefined
        })) : []
      };
      setCampaign(transformedCampaign);
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Erro ao carregar campanha');
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>;
  }
  if (!campaign) {
    return <div className="container mx-auto py-8">
        <h1>Campanha não encontrada</h1>
      </div>;
  }
  const progress = campaign.current_amount / campaign.goal_amount * 100;
  const isOwner = user?.id === campaign.user_id;
  return <div className="container mx-auto py-8">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/apoia')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{campaign.title}</CardTitle>
              <Badge variant={new Date(campaign.deadline) > new Date() ? 'default' : 'secondary'}>
                {new Date(campaign.deadline) > new Date() ? 'Em andamento' : 'Finalizada'}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <img src={campaign.cover_image_url || 'https://via.placeholder.com/800x400'} alt={campaign.title} className="w-full rounded-lg aspect-video object-contain" />
              <Progress value={progress} />
              <div className="flex justify-between text-sm">
                <span>R$ {campaign.current_amount.toLocaleString('pt-BR')}</span>
                <span>{Math.round(progress)}% da meta</span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Descrição</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{campaign.description}</p>
              </div>

              {isOwner && <Button onClick={() => navigate(`/apoia/${id}/edit`)}>
                  Editar Campanha
                </Button>}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-bold mb-4">Formas de Contribuir</h2>
          {campaign.contribution_tiers?.map((tier, index) => <Card key={index}>
              <CardHeader>
                <CardTitle className="text-lg">{tier.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">{tier.description}</p>
                <div className="text-lg font-bold">
                  R$ {tier.amount.toLocaleString('pt-BR')}
                </div>
                {tier.rewards && <div className="text-sm">
                    <strong>Recompensa:</strong> {tier.rewards}
                  </div>}
                <Button className="w-full" variant="default">
                  Contribuir
                </Button>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </div>;
}